import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type Map from 'ol/Map';
import type BaseLayer from 'ol/layer/Base';
import type VectorSource from 'ol/source/Vector';
import type Feature from 'ol/Feature';
import {
  Check, CornerDownRight, Link2, Loader2, MapPin, Minus, MousePointer2, Move, PenTool, Pentagon, Redo2, Route, Trash2,
  Undo2, X, ListPlus,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import type { CompanyLayerField } from '@/services/workspace.service';
import { EditSession, DrawKind, EditTool, LayerShape, OverlapAnswer, OverlapQuestion, SessionState, valuesOf } from './EditSession';
import { EditApi, EditError, ERROR_KEYS } from './editingApi';
import AttributeForm, { Values } from './AttributeForm';
import OverlapDialog, { areaText } from './OverlapDialog';

/**
 * The tools of one editing session on the map (see {@link EditSession}): a toolbar at the bottom of the map, the
 * attribute form after a feature is drawn, the overlap question, and a line saying what the tool does and what just
 * happened. Made by the app's editing host for a company layer, and by the browser harness for a stub map.
 */

export interface WorkbenchProps {
  map: Map;
  source: VectorSource;
  shape: LayerShape;
  title: string;
  fields: CompanyLayerField[];
  api: EditApi;
  lang: string;
  hideLayer?: BaseLayer;
  onClose: () => void;
  onSaved?: () => void;
  /** A message for a toast as well as the status line (the app's), already worded. */
  notify?: (kind: 'info' | 'error' | 'success', text: string) => void;
  /** The session, for whoever made the workbench (the harness drives it). */
  onSession?: (s: EditSession | null) => void;
}

interface Asked<Q, A> {
  question: Q;
  resolve: (a: A) => void;
}

const btn = 'flex items-center justify-center shrink-0 w-8 h-8 p-0 rounded-md text-gn-secondary hover:bg-gn-hover disabled:opacity-35 disabled:hover:bg-transparent';
const on = 'bg-gn-blue text-gn-inverse hover:bg-gn-blue';

export const EditingWorkbench: React.FC<WorkbenchProps> = (props) => {
  const { map, source, shape, title, fields, api, lang, hideLayer, onClose, onSaved, notify, onSession } = props;
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState<EditSession | null>(null);
  const [attributes, setAttributes] = useState<Asked<{ initial: Values; isNew: boolean }, Values | null> | null>(null);
  const [overlap, setOverlap] = useState<Asked<OverlapQuestion, OverlapAnswer> | null>(null);
  const [message, setMessage] = useState<{ kind: 'info' | 'error' | 'success'; text: string } | null>(null);
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  // The session lives as long as the map, source and layer do; a new api object for the same layer (a parent that
  // made it again while rendering) must not start a new session and lose what was drawn.
  const apiRef = useRef(api);
  apiRef.current = api;

  const say = useCallback((kind: 'info' | 'error' | 'success', text: string) => {
    setMessage({ kind, text });
    notifyRef.current?.(kind, text);
  }, []);

  const failed = useCallback((e: EditError) => {
    const key = ERROR_KEYS[e.code] ?? 'editing.errors.failed';
    const params: Record<string, unknown> = { message: e.message };
    if (typeof e.body.distance === 'number') params.distance = e.body.distance.toFixed(1);
    if (typeof e.body.tolerance === 'number') params.tolerance = e.body.tolerance.toFixed(1);
    if (Array.isArray(e.body.problems)) params.message = e.body.problems.map((p: any) => `${String(p.where).split('.').pop()}: ${p.message}`).join('; ');
    if (Array.isArray(e.body.fields)) params.message = e.body.fields.join(', ');
    say('error', t(key, params));
  }, [say, t]);

  useEffect(() => {
    const s = new EditSession(map, source, {
      shape,
      api: {
        check: (...a) => apiRef.current.check(...a),
        create: (...a) => apiRef.current.create(...a),
        update: (...a) => apiRef.current.update(...a),
        remove: (...a) => apiRef.current.remove(...a),
        joinPreview: (...a) => apiRef.current.joinPreview(...a),
      },
      hideLayer,
      hooks: {
        askOverlap: (question) => new Promise((resolve) => setOverlap({ question, resolve })),
        askAttributes: (feature: Feature, isNew: boolean) =>
          new Promise((resolve) => setAttributes({ question: { initial: valuesOf(feature), isNew }, resolve })),
        notify: (kind, key, params) => say(kind, t(key, params)),
        failed,
      },
    });
    setSession(s);
    onSession?.(s);
    return () => {
      s.dispose();
      onSession?.(null);
      setSession(null);
    };
  }, [map, source, shape, hideLayer]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return null;
  return (
    <>
      <Toolbar session={session} shape={shape} title={title} onClose={onClose} onSaved={onSaved} message={message} />
      {attributes && (
        <AttributeForm fields={fields} initial={attributes.question.initial} isNew={attributes.question.isNew} lang={lang}
                       onDone={(v) => { const r = attributes.resolve; setAttributes(null); r(v); }} />
      )}
      {overlap && (
        <OverlapDialog question={overlap.question} locale={i18n.language || 'en'}
                       onAnswer={(a) => { const r = overlap.resolve; setOverlap(null); r(a); }} />
      )}
    </>
  );
};

const Toolbar: React.FC<{
  session: EditSession;
  shape: LayerShape;
  title: string;
  onClose: () => void;
  onSaved?: () => void;
  message: { kind: 'info' | 'error' | 'success'; text: string } | null;
}> = ({ session, shape, title, onClose, onSaved, message }) => {
  const { t } = useTranslation();
  const state: SessionState = useSyncExternalStore((l) => session.subscribe(l), () => session.getState());
  const lines = shape === 'line' || shape === 'any';

  const tools: { tool: EditTool; icon: React.ReactNode; label: string; show: boolean }[] = [
    { tool: 'add', icon: <PenTool size={16} />, label: t('editing.tool_add'), show: true },
    { tool: 'modify', icon: <MousePointer2 size={16} />, label: t('editing.tool_modify'), show: true },
    { tool: 'move', icon: <Move size={16} />, label: t('editing.tool_move'), show: true },
    { tool: 'delete', icon: <Trash2 size={16} />, label: t('editing.tool_delete'), show: true },
    { tool: 'continue', icon: <CornerDownRight size={16} />, label: t('editing.tool_continue'), show: lines },
    { tool: 'join', icon: <Link2 size={16} />, label: t('editing.tool_join'), show: lines },
  ];
  const kinds: { kind: DrawKind; icon: React.ReactNode; label: string }[] = [
    { kind: 'Point', icon: <MapPin size={14} />, label: t('editing.kind_point') },
    { kind: 'LineString', icon: <Minus size={14} />, label: t('editing.kind_line') },
    { kind: 'Polygon', icon: <Pentagon size={14} />, label: t('editing.kind_polygon') },
  ];

  const hint = {
    add: state.drawing
      ? (state.drawKind === 'Point' ? t('editing.hint_add_point') : t('editing.hint_drawing'))
      : state.drawKind === 'Point' ? t('editing.hint_add_point') : state.drawKind === 'LineString' ? t('editing.hint_add_line') : t('editing.hint_add_polygon'),
    modify: t('editing.hint_modify'),
    move: t('editing.hint_move'),
    delete: t('editing.hint_delete'),
    continue: state.drawing ? t('editing.hint_drawing') : t('editing.hint_continue'),
    join: state.picks === 1 ? t('editing.hint_join_second') : t('editing.hint_join'),
  }[state.tool];

  const close = () => {
    if (session.dirty && !window.confirm(t('editing.confirm_close'))) return;
    onClose();
  };
  const discard = () => {
    if (!window.confirm(t('editing.confirm_discard'))) return;
    session.discard();
  };
  const save = async () => {
    if (await session.save()) onSaved?.();
  };

  return (
    <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-[60] w-max max-w-[calc(100vw-1rem)]" data-testid="editing-toolbar">
      <div className="flex flex-col items-stretch gap-1 rounded-xl border border-gn-light bg-gn-surface shadow-gn-lg px-2 py-1.5">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="px-1.5 text-[11px] font-semibold text-gn-primary max-w-[10rem] truncate" title={title}>{title}</span>
          <span className="w-px h-5 bg-gn-tertiary mx-0.5" />
          {tools.filter((x) => x.show).map((x) => (
            <button key={x.tool} type="button" title={x.label} aria-label={x.label} aria-pressed={state.tool === x.tool}
                    data-testid={`tool-${x.tool}`} className={cn(btn, state.tool === x.tool && on)} disabled={state.busy}
                    onClick={() => session.setTool(x.tool)}>
              {x.icon}
            </button>
          ))}
          {shape === 'any' && state.tool === 'add' && (
            <>
              <span className="w-px h-5 bg-gn-tertiary mx-0.5" />
              {kinds.map((k) => (
                <button key={k.kind} type="button" title={k.label} aria-label={k.label} aria-pressed={state.drawKind === k.kind}
                        data-testid={`kind-${k.kind}`} className={cn(btn, 'w-7 h-7', state.drawKind === k.kind && on)}
                        onClick={() => session.setDrawKind(k.kind)}>
                  {k.icon}
                </button>
              ))}
            </>
          )}
          {(state.tool === 'add' || state.tool === 'continue') && state.drawKind !== 'Point' && (
            <button type="button" title={t('editing.trace')} aria-label={t('editing.trace')} aria-pressed={state.trace}
                    data-testid="trace" className={cn(btn, state.trace && on)} onClick={() => session.setTrace(!state.trace)}>
              <Route size={16} />
            </button>
          )}
          {state.tool === 'modify' && state.selected && (
            <button type="button" title={t('editing.attributes')} aria-label={t('editing.attributes')} data-testid="edit-attributes"
                    className={btn} onClick={() => { void session.editAttributes(); }}>
              <ListPlus size={16} />
            </button>
          )}
          <span className="w-px h-5 bg-gn-tertiary mx-0.5" />
          <button type="button" title={t('editing.undo')} aria-label={t('editing.undo')} data-testid="undo" className={btn}
                  disabled={!state.canUndo && !state.drawing} onClick={() => session.undo()}>
            <Undo2 size={16} />
          </button>
          <button type="button" title={t('editing.redo')} aria-label={t('editing.redo')} data-testid="redo" className={btn}
                  disabled={!state.canRedo} onClick={() => session.redo()}>
            <Redo2 size={16} />
          </button>
          <span className="w-px h-5 bg-gn-tertiary mx-0.5" />
          <button type="button" data-testid="save" disabled={state.pending === 0 || state.busy} onClick={() => { void save(); }}
                  className="flex items-center gap-1 h-8 px-2.5 rounded-md bg-gn-blue text-gn-inverse text-xs font-medium disabled:opacity-40">
            {state.busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {t('editing.save')}
            {state.pending > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-white/25 text-[10px]" data-testid="pending">{state.pending}</span>}
          </button>
          <button type="button" data-testid="discard" disabled={state.pending === 0 || state.busy} onClick={discard}
                  className="h-8 px-2 rounded-md text-xs text-gn-secondary hover:bg-gn-hover disabled:opacity-40">
            {t('editing.discard')}
          </button>
          <button type="button" title={t('editing.close')} aria-label={t('editing.close')} data-testid="close-editing" className={btn}
                  onClick={close}>
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center gap-2 px-1 text-[11px] min-h-[16px]">
          <span className="text-gn-muted truncate" data-testid="editing-hint">{hint}</span>
          <span className={cn('ml-auto shrink-0 font-medium', state.snapped ? 'text-[#d946ef]' : 'text-transparent')} data-testid="snap-state">
            {state.snapped === 'edge' ? t('editing.snapped_edge') : state.snapped === 'vertex' ? t('editing.snapped_vertex') : '·'}
          </span>
        </div>
        {message && (
          <div data-testid="editing-message" data-kind={message.kind}
               className={cn('px-1 pb-0.5 text-[11px] truncate', message.kind === 'error' ? 'text-gn-danger' : message.kind === 'success' ? 'text-gn-success' : 'text-gn-secondary')}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export { areaText };
export default EditingWorkbench;
